import { faComment } from '@fortawesome/free-solid-svg-icons';
import { useUpdateProperty } from '../../queries/properties';
import { useAgentShortName } from '../../queries/agents';
import { useProspects } from '../../queries/prospects';
import { useAuth } from '../../context/AuthContext';
import { isVacant, prospectsFor } from '../../utils/property';
import { HOME_TYPES, formatMoney } from '../Properties/constants';
import { Btn, Chip } from '../Todos/ui';

const HOME_LABEL = Object.fromEntries(HOME_TYPES.map((t) => [t.value, t.label]));
const areaOf = (u) => u.area || u.address?.city || '--';
const bb = (u) => (u.bedrooms ? `${u.bedrooms} / ${u.bathrooms || 0}` : u.unitType === 'room' ? 'Room' : '--');

export default function VacanciesTab({ units, phone, onOpenUnit }) {
  const { rows: prospects } = useProspects();
  const agent = useAgentShortName();
  const update = useUpdateProperty();
  const { can } = useAuth();
  const canManage = can('manage_listings');

  const rows = units.filter(isVacant);
  const listed = rows.filter((u) => u.listed).length;

  const prosLabel = (u) => {
    const n = prospectsFor(prospects, u.name).length;
    return n ? `${n} in pipeline` : 'None yet';
  };
  const toggle = (u) => (e) => {
    e.stopPropagation();
    update.mutate({ _id: u._id, listed: !u.listed });
  };
  // Status + a List/Unlist toggle so Maggie's quoting is managed right here.
  const quotes = (u) => (
    <div className="m-row gap8" onClick={(e) => e.stopPropagation()}>
      {u.listed ? (
        <Chip fam="ok" icon={faComment}>
          Working
        </Chip>
      ) : (
        <span className="m-sub">Not listed</span>
      )}
      {canManage && (
        <Btn sm kind={u.listed ? undefined : 'primary'} onClick={toggle(u)}>
          {u.listed ? 'Unlist' : 'List it'}
        </Btn>
      )}
    </div>
  );

  const body = phone ? (
    <div className="m-col gap8">
      {rows.map((u) => (
        <div key={u._id} role="button" tabIndex={0} className="m-crow" onClick={() => onOpenUnit(u)} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && onOpenUnit(u)}>
          <div className="m-row between">
            <span className="b sm">{u.name}</span>
            <span className="b num">{formatMoney(u.rent) || '--'}</span>
          </div>
          <div className="m-sub">{[HOME_LABEL[u.unitType], bb(u), areaOf(u)].filter(Boolean).join(' · ')}</div>
          <div className="m-row between">
            <span className="m-sub">{prosLabel(u)}</span>
            {quotes(u)}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="m-tw">
      <table className="m-tbl">
        <thead>
          <tr>
            {['Vacant unit', 'Area', 'Type', 'Beds / baths', 'Rent', 'Prospects', `${agent} quotes`].map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((u) => (
              <tr key={u._id} className="click" tabIndex={0} onClick={() => onOpenUnit(u)} onKeyDown={(e) => e.key === 'Enter' && onOpenUnit(u)}>
                <td>
                  <span className="b">{u.name}</span>
                </td>
                <td>{areaOf(u)}</td>
                <td>{HOME_LABEL[u.unitType] || '--'}</td>
                <td>
                  <span className="num">{bb(u)}</span>
                </td>
                <td>
                  <span className="b num">{formatMoney(u.rent) || '--'}</span>
                </td>
                <td>{prosLabel(u)}</td>
                <td>{quotes(u)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="m-empty">
                No vacant units right now.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="m-col gap12">
      <div className="m-sub">
        {rows.length} vacant units · {listed} listed for {agent} to quote · List or unlist a unit to control what she offers
      </div>
      {body}
    </div>
  );
}
